import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../store/app.state';
import { Actions, ofType } from '@ngrx/effects';
import { PreloaderColor } from '@enums/shared.enum';
import { SharedModels } from '@share-models';
import { features } from '@consts/text.const';
import { map } from 'rxjs/operators';
import { filter, take } from 'rxjs';
import { VehicleGroupsDialogService } from '../../../../../../services/vehicle-groups-dialog.service';
import { VehiclesTableComponent } from '../../components/vehicles-table/vehicles-table.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UiButtonComponent, UiNoDataComponent, UiPreloaderComponent, UiSelectedItemsSnackbarComponent } from '@ui';
import { vehicleGroupsFeature } from '@states/vehicle-groups/vehicle-groups.feature';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { VehicleGroupsActions } from '@states/vehicle-groups/vehicle-groups.actions';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { VehicleModels } from '@models/vehicle.model';

@Component({
  selector: 'app-vehicle-group-details',
  imports: [
    VehiclesTableComponent,
    TranslatePipe,
    UiButtonComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiSelectedItemsSnackbarComponent,
  ],
  templateUrl: './vehicle-group-details.component.html',
  styleUrl: './vehicle-group-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleGroupDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly store$ = inject(Store<AppState>);
  private readonly vehicleGroupsDialogService = inject(VehicleGroupsDialogService);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly membersTable = viewChild(VehiclesTableComponent);

  protected readonly loaderColor = PreloaderColor;
  protected readonly viewStates = SharedModels.ViewState;
  protected readonly noData = features.vehicleGroup;
  protected readonly removeFromGroupTooltip = 'VEHICLE_GROUPS.DETAIL.REMOVE_FROM_GROUP';

  protected readonly groupId = toSignal(
    this.route.paramMap.pipe(map((p) => p.get('groupId'))),
    { initialValue: this.route.snapshot.paramMap.get('groupId') },
  );

  private readonly vehicleGroupDetailsState = this.store$.selectSignal(vehicleGroupsFeature.selectVehicleGroupDetails);
  protected readonly vehicleGroupDetails = computed(() => this.vehicleGroupDetailsState().document);
  protected readonly isVehicleGroupDetailsLoading = computed(() => this.vehicleGroupDetailsState().isLoading);

  private readonly vehicleGroupMembersState = this.store$.selectSignal(vehicleGroupsFeature.selectVehicleGroupMembers);
  protected readonly members = this.store$.selectSignal(vehicleGroupsFeature.selectAllVehicleGroupMembers);
  protected readonly isVehicleGroupMembersLoading = computed(() => this.vehicleGroupMembersState().isLoading);

  protected readonly group = computed((): VehicleGroupsModel.VehicleGroupDetailsDocument | undefined => {
    const id = this.groupId();
    if (id == null || id === '') {
      return undefined;
    }
    const group = this.vehicleGroupDetails();
    if (!group || group._id !== id) {
      return undefined;
    }
    return group;
  });

  protected readonly canRenameVehicleGroup = computed(
    () => this.group()?.activeOrgAccessRole === VehicleGroupsModel.ShareAccessRole.Admin,
  );

  protected readonly canEditGroupMembers = computed(() => {
    const role = this.group()?.activeOrgAccessRole;
    return (
      role === VehicleGroupsModel.ShareAccessRole.Admin ||
      role === VehicleGroupsModel.ShareAccessRole.Contributor
    );
  });

  protected readonly membersViewState = computed<SharedModels.ViewState>(() => {
    if (this.isVehicleGroupDetailsLoading() || this.isVehicleGroupMembersLoading()) {
      return SharedModels.ViewState.INITIAL_EMPTY;
    }
    return this.members().length > 0
      ? SharedModels.ViewState.HAS_CONTENT
      : SharedModels.ViewState.INITIAL_EMPTY;
  });

  protected readonly selectedPlatesCount = computed(
    () => this.membersTable()?.selectedPlatesRows().size ?? 0,
  );

  public ngOnInit(): void {
    const id = this.groupId();
    if (!id) {
      return;
    }
    this.store$.dispatch(VehiclesActions.getVehicles());
    this.store$.dispatch(VehicleGroupsActions.startLoadingVehicleGroupDetails({ groupId: id }));
  }

  protected editName(): void {
    if (!this.canRenameVehicleGroup()) {
      return;
    }
    const g = this.group();
    if (!g) {
      return;
    }
    this.vehicleGroupsDialogService.openEdit(g._id, g.name);
  }

  protected shareGroup(): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const g = this.group();
    if (!g) {
      return;
    }
    this.vehicleGroupsDialogService.openShare(g._id, g.name, g.createdByOrgId);
  }

  protected addVehicles(): void {
    if (!this.canEditGroupMembers()) {
      return;
    }
    const g = this.group();
    if (!g) {
      return;
    }
    const memberIds = this.members()
      .map((m) => m.id)
      .filter((id): id is number => id != null);
    this.vehicleGroupsDialogService.openAddVehiclesToGroup(g._id, g.name, memberIds);
  }

  protected removeMemberFromGroup = (vehicle: VehicleModels.Vehicle): void => {
    if (!this.canEditGroupMembers() || vehicle.id == null) {
      return;
    }
    this.removeMembersByVehicleIds([vehicle.id], () => {
      this.clearPlateFromSelection(vehicle.plate);
    });
  };

  protected selectAllDisplayedVehicles(): void {
    const table = this.membersTable();
    if (!table) {
      return;
    }

    const displayedPlates = this.members()
      .map((vehicle) => String(vehicle.plate ?? '').trim())
      .filter((plate) => plate.length > 0);
    const selected = table.selectedPlatesRows();
    const missingDisplayedPlates = displayedPlates.filter((plate) => !selected.has(plate));

    if (missingDisplayedPlates.length === 0) {
      return;
    }

    table.applyForcedSelection([...selected, ...missingDisplayedPlates]);
  }

  protected clearSelectedVehicles(): void {
    this.membersTable()?.applyForcedSelection([]);
  }

  protected removeSelectedMembers(): void {
    const selectedPlates = this.membersTable()?.selectedPlatesRows() ?? new Set<string>();
    const vehicleIds = this.members()
      .filter((vehicle) => selectedPlates.has(vehicle.plate))
      .map((vehicle) => vehicle.id)
      .filter((id): id is number => id != null);

    this.removeMembersByVehicleIds(vehicleIds, () => {
      this.clearSelectedVehicles();
    });
  }

  private clearPlateFromSelection(plate: string): void {
    const table = this.membersTable();
    if (!table) {
      return;
    }
    const selected = table.selectedPlatesRows();
    if (!selected.has(plate)) {
      return;
    }
    const nextSelected = [...selected].filter((selectedPlate) => selectedPlate !== plate);
    table.applyForcedSelection(nextSelected);
  }

  private removeMembersByVehicleIds(vehicleIds: number[], onSuccess: () => void): void {
    if (!this.canEditGroupMembers()) {
      return;
    }

    const group = this.group();

    if (!group || vehicleIds.length === 0) {
      return;
    }

    const requestId = `${group._id}_${Date.now()}_${Math.random()}`;

    this.actions$
      .pipe(
        ofType(VehicleGroupsActions.removeVehiclesFromGroupSuccess),
        filter((action) => action.requestId === requestId),
        takeUntilDestroyed(this.destroyRef),
        take(1),
      )
      .subscribe(() => {
        onSuccess();
      });

    this.store$.dispatch(
      VehicleGroupsActions.requestRemoveVehiclesFromGroup({
        groupId: group._id,
        groupName: group.name,
        vehicleIds,
        requestId,
      }),
    );
  }
}
